import React from 'react'
import { useList } from '@refinedev/core'
import { brand } from '../../theme/brand'
import { ListPage, SectionCard, EmptyHint, TableSurface, type Stat } from '../../theme/listkit'
import { BookOpen, UsersRound } from '../../components/icons'

interface DevotionalRead {
  id: string
  user_id: string
  read_on: string
  created_at: string
}

interface ReadsSummary {
  total_reads: number | null
  distinct_members: number | null
}

const fmtDate = (v: string | null) =>
  v
    ? new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : '—'

const fmtWhen = (v: string | null) =>
  v
    ? new Date(v).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '—'

/**
 * Devotional Reads — read-only engagement visibility into the devotional-streak
 * feature (devotional_reads table), ordered by read date, newest first. This
 * single useList is the only data source, so liveMode 'off' is REQUIRED to avoid
 * a double subscription.
 */
export function DevotionalReadsList() {
  // Primary list: a BOUNDED page of the most-recent reads (was mode:'off').
  const { result } = useList<DevotionalRead>({
    resource: 'devotional_reads',
    pagination: { pageSize: 200 },
    liveMode: 'off',
    sorters: [{ field: 'read_on', order: 'desc' }],
  })
  const rows = result?.data ?? []

  // Stat deck: a single pre-aggregated row from the admin summary VIEW instead of
  // counting/Set-ing over the whole devotional_reads table on the main thread.
  const { result: summaryRes } = useList<ReadsSummary>({
    resource: 'admin_devotional_reads_summary',
    pagination: { pageSize: 1 },
    liveMode: 'off',
  })
  const summary = summaryRes?.data?.[0]

  const totalReads = summary?.total_reads ?? rows.length
  const uniqueMembers = summary?.distinct_members ?? new Set(rows.map((r) => r.user_id)).size

  const stats: Stat[] = [
    { label: 'Total Reads', value: totalReads.toLocaleString(), icon: BookOpen, tone: 'jaguar' },
    { label: 'Members', value: uniqueMembers.toLocaleString(), icon: UsersRound, tone: 'green' },
  ]

  return (
    <ListPage
      intro={{
        eyebrow: 'Content · Devotionals',
        description:
          'Each day a member marks a devotional read, building their streak. A read-only window into the daily devotional habit across the community.',
      }}
      stats={stats}
      statColumns={2}
    >
      <SectionCard title="Devotional Reads" sub="By read date · newest first">
        {rows.length === 0 ? (
          <EmptyHint>No devotional reads recorded yet.</EmptyHint>
        ) : (
          <TableSurface>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Member', 'Read On', 'Recorded'].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: 'left',
                        padding: '12px 16px',
                        fontFamily: brand.fSans,
                        fontWeight: 700,
                        fontSize: 11,
                        textTransform: 'uppercase',
                        letterSpacing: '.05em',
                        color: brand.sub,
                        borderBottom: '1px solid ' + brand.cardLine,
                        background: brand.paper,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={r.id}
                    style={{
                      borderBottom: i < rows.length - 1 ? '1px solid ' + brand.cardLine : 'none',
                    }}
                  >
                    <td
                      style={{
                        padding: '12px 16px',
                        fontFamily: brand.fSans,
                        fontWeight: 500,
                        fontSize: 12.5,
                        color: brand.ink,
                      }}
                    >
                      {r.user_id}
                    </td>
                    <td
                      style={{
                        padding: '12px 16px',
                        fontFamily: brand.fSans,
                        fontWeight: 600,
                        fontSize: 12.5,
                        color: brand.ink,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {fmtDate(r.read_on)}
                    </td>
                    <td
                      style={{
                        padding: '12px 16px',
                        fontFamily: brand.fSans,
                        fontWeight: 500,
                        fontSize: 12.5,
                        color: brand.sub,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {fmtWhen(r.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableSurface>
        )}
      </SectionCard>
    </ListPage>
  )
}
